<script setup lang="ts">
const route = useRoute()
const slug = Array.isArray(route.params.broker_slug) ? route.params.broker_slug.join('/') : route.params.broker_slug

const { fetchBroker, fetchBrokerProfile, fetchBrokerReviews } = useBroker()

const { data: broker } = await useAsyncData(`broker-data-${slug}`, async () => {
  return fetchBroker(`${slug}`)
})

// Bestaat de makelaar niet (of is hij verborgen)? Geef een echte 404 i.p.v. een
// lege "soft 404"-pagina, zodat Google verouderde broker-URL's netjes dropt.
if (!broker.value) {
  throw createError({ statusCode: 404, statusMessage: 'Makelaar niet gevonden', fatal: true })
}

const { data: brokerProfileData } = await useAsyncData(`broker-profile-${slug}`, async () => {
  return fetchBrokerProfile(`${slug}`)
})

const { data: brokerReviewsData } = await useAsyncData(`broker-reviews-${slug}`, async () => {
  return fetchBrokerReviews(`${slug}`)
})

// Display title for H1 - always full, no truncation
const displayTitle = computed(() => {
  if (!broker.value?.broker_name) return 'Makelaar Profiel'
  return `${broker.value.broker_name}`
})

// SEO title for <title> tag - truncated to stay under 60 chars
const SITE_SUFFIX_LENGTH = 19
const MAX_TITLE_LENGTH = 60

const seoTitle = computed(() => {
  if (!broker.value?.broker_name) return 'Makelaar Profiel'

  const name = broker.value.broker_name
  const year = new Date().getFullYear()
  const fullSuffix = ` Reviews & Tarieven (${year})`
  const shortSuffix = ` | Reviews (${year})`

  if (name.length + fullSuffix.length + SITE_SUFFIX_LENGTH <= MAX_TITLE_LENGTH) {
    return `${name}${fullSuffix}`
  }

  // Naam nooit afkappen: truncatie maakte titles van lange namen identiek
  // (duplicate titles). Een title boven de 60 tekens is onschuldig; Google
  // kapt hem zelf af in de weergave.
  return `${name}${shortSuffix}`
})

const description = computed(() => {
  if (!broker.value) return 'Gedetailleerde informatie en statistieken over deze makelaar.'

  const b = broker.value
  const bp = brokerProfileData.value

  const ratingPart = bp?.avg_rating && bp?.total_reviews
    ? ` ✓ ${bp.avg_rating.toFixed(1)}/5 beoordeling`
    : ''

  return `${b.broker_name}: ${b.total_houses_sold} woningen verkocht ✓ Gem. prijs ${b.avg_house_price} ✓ ${b.avg_days_on_market} dagen verkooptijd${ratingPart}. Vergelijk tarieven & reviews.`
})

// Zichtbare intro onder de H1, bewust anders dan de meta-description
const heroDescription = computed(() => {
  const b = broker.value
  if (!b) return ''
  return `${b.total_houses_sold} woningen verkocht, gemiddeld in ${b.avg_days_on_market} dagen en voor ${b.avg_house_price}. Bekijk reviews, tarieven en waar ${b.broker_name} het meest verkoopt.`
})

const breadcrumbs = computed(() => [
  { label: 'Home', to: '/', icon: 'i-heroicons-home' },
  { label: 'Makelaars', to: '/makelaars/' },
  ...(broker.value?.city_name
    ? [{ label: broker.value.city_name, to: broker.value.location_url }]
    : []),
  { label: broker.value?.broker_name, to: `/makelaar/${slug}/` },
])

useSeoMeta({
  title: seoTitle,
  description,
  ogTitle: seoTitle,
  ogDescription: description,
  ogType: 'profile',
})

// Google Places-adres: "Goudsesingel 71, 3031 EE Rotterdam, Nederland"
const parseAddress = (address: string, fallbackCity: string) => {
  const [street, postalCity] = address.split(', ')
  const match = postalCity?.match(/^(\d{4}\s?[A-Z]{2})\s+(.+)$/)
  return {
    '@type': 'PostalAddress',
    'streetAddress': street,
    ...(match?.[1] && { postalCode: match[1] }),
    'addressLocality': match?.[2] ?? fallbackCity,
    'addressCountry': 'NL',
  }
}

const b = broker.value
const bp = brokerProfileData.value
const office = bp?.locations?.[0]

useSchemaOrg([
  defineWebPage({
    '@type': 'ProfilePage',
    'name': seoTitle.value,
    ...(b.lastmod && { dateModified: b.lastmod }),
    'author': { '@type': 'Organization', 'name': 'Juiste Makelaar', 'url': 'https://juistemakelaar.nl/' },
    'publisher': { '@type': 'Organization', 'name': 'Juiste Makelaar', 'url': 'https://juistemakelaar.nl/' },
  }),
  defineLocalBusiness({
    // Eigen @id: zonder deze neemt LocalBusiness `#identity` over van de site-Organization
    '@id': `https://juistemakelaar.nl/makelaar/${slug}/#makelaar`,
    '@type': 'RealEstateAgent',
    'name': b.broker_name,
    'description': `Makelaar met ${b.total_houses_sold} verkochte woningen. Actief in het ${b.price_segment}.`,
    ...(office?.website && { url: office.website }),
    ...(office?.phone && { telephone: office.phone }),
    'address': office?.address ? parseAddress(office.address, b.city_name) : { '@type': 'PostalAddress', 'addressLocality': b.city_name, 'addressCountry': 'NL' },
    'priceRange': `${b.broker_cost_min} - ${b.broker_cost_max}`,
    'areaServed': b.top_5_locations?.map(loc => loc?.name).filter(Boolean).join(', '),
    ...(bp?.avg_rating && bp.total_reviews && {
      aggregateRating: {
        '@type': 'AggregateRating',
        'ratingValue': bp.avg_rating.toFixed(1),
        'bestRating': 5,
        'worstRating': 1,
        'ratingCount': bp.total_reviews,
      },
    }),
    ...(bp?.top_reviews?.length && {
      review: bp.top_reviews.slice(0, 5).map(r => ({
        '@type': 'Review',
        'author': { '@type': 'Person', 'name': r.reviewer_name },
        'datePublished': r.review_date,
        'reviewBody': r.review_text,
        'reviewRating': {
          '@type': 'Rating',
          'ratingValue': r.rating,
          'bestRating': 5,
          'worstRating': 1,
        },
      })),
    }),
  }),
])

useBreadcrumbSchema(breadcrumbs)

useOgImage(seoTitle)

// Eén vaste balk onderaan, nooit twee: de site-brede `CtaMobileBar` houdt zich
// stil zolang deze pagina zijn eigen onderbalk toont (`useBottomBar`).
const showMobileCta = useBottomBar('page')

const { $posthog } = useNuxtApp()
const trackMobileCta = () => {
  $posthog()?.capture('broker_cta_clicked', { placement: 'mobile_sticky', broker_slug: slug })
}
</script>

<template>
  <div>
    <template v-if="broker">
      <!-- Page hero -->
      <div class="bg-muted border-b border-default">
        <UContainer class="py-10 lg:py-14">
          <UBreadcrumb
            :items="breadcrumbs"
            class="mb-6"
          />
          <h1 class="text-3xl lg:text-4xl font-bold tracking-[-0.015em] text-highlighted text-balance mb-3">
            {{ displayTitle }}
          </h1>
          <p class="text-muted text-pretty max-w-2xl">
            {{ heroDescription }}
          </p>
        </UContainer>
      </div>

      <UContainer>
        <UPage>
          <UPageBody class="prose">
            <DynamicBrokerContent
              :broker="broker"
              :broker-profile="brokerProfileData"
              :location-reviews="brokerReviewsData?.locations ?? []"
            />
          </UPageBody>

          <template #right>
            <div class="hidden lg:block relative">
              <div class="mt-12 sticky top-24 z-10">
                <SidebarCompareAgents
                  :location-name="broker.city_name"
                  class="static"
                />
              </div>
            </div>
          </template>
        </UPage>

        <BrokerSimilarBrokersComparison :broker="broker" />
      </UContainer>

      <!-- Mobile sticky CTA bar -->
      <div
        v-if="showMobileCta"
        class="fixed bottom-0 left-0 right-0 z-40 lg:hidden bg-default border-t border-default px-4 py-3 safe-area-pb"
      >
        <UButton
          color="secondary"
          size="xl"
          trailing-icon="i-brand-arrow-alt"
          to="/vergelijk-verkoop/"
          class="w-full justify-center"
          @click="trackMobileCta"
        >
          Vergelijk makelaars
        </UButton>
      </div>
    </template>
  </div>
</template>
